import { useTimeZone } from '@/auth/useTimeZone.js';
import { getNowPlainDateISO } from '@/lib/time-zones.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { Schedule, UpdateScheduleOptions } from '@airreading/common/data';

import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { createSchedule, getSchedules, updateSchedule } from '@/lib/api-calls.js';
import { isNotErrorString } from '@/lib/common-utils.js';
import { parseIntoScheduleBreak } from '@/lib/date-utils.js';
import { textAreaValueToArray } from '@/lib/string-utils.js';

import { useAllSchools } from '@/lib/api-call-hooks.js';
import { validatePlainDateIntervalLike } from '@airreading/common';
import { formatScheduleBreakInput } from './formatting.js';

interface CreateEditScheduleProps {
  trigger?: ReactNode;
  modalTitle?: string;
  from?: {
    fromSchedule: Schedule;
    mode: 'edit' | 'create';
  };
  afterSave?: () => void;
}

const createEditScheduleFormSchema = z.object({
  scheduleBreaks: z
    .string()
    .optional()
    .superRefine((input, ctx) => {
      const scheduleBreaks = textAreaValueToArray(input);
      if (!scheduleBreaks.length) {
        return;
      }

      for (const scheduleBreak of scheduleBreaks) {
        const result = parseIntoScheduleBreak(scheduleBreak);
        if (typeof result === 'string') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result,
          });
          return;
        }
      }
    }),
  startDate: z.instanceof(Temporal.PlainDate),
  endDate: z.instanceof(Temporal.PlainDate),
  practiceDate: z.instanceof(Temporal.PlainDate).nullable(),
  diagnosticDate: z.instanceof(Temporal.PlainDate).nullable(),
  schoolId: z.string(),
  name: z.string(),
});

const toDefaultValue = (
  preferredTimeZone: string | null | undefined,
  fromSchedule?: Schedule,
  mode = 'create',
): DefaultValues<z.infer<typeof createEditScheduleFormSchema>> => {
  return {
    name: fromSchedule?.name ? `${fromSchedule.name}${mode === 'create' ? ' - copy' : ''}` : '',
    schoolId: fromSchedule?.schoolId,
    startDate: fromSchedule?.startPlainDate ?? getNowPlainDateISO(preferredTimeZone),
    endDate: fromSchedule?.endPlainDate ?? getNowPlainDateISO(preferredTimeZone).add({ days: 1 }),
    practiceDate: fromSchedule?.practicePlainDate ?? null,
    diagnosticDate: fromSchedule?.diagnosticPlainDate ?? null,
    scheduleBreaks: (fromSchedule?.scheduleBreaks || []).map(formatScheduleBreakInput).join('\n'),
  };
};

export const CreateEditSchedule = ({ trigger, modalTitle, from, afterSave }: CreateEditScheduleProps) => {
  const { timeZone } = useTimeZone();
  const { data: allSchools = [], isPending: isLoadingSchools } = useAllSchools();

  const { mutateAsync: createScheduleMutate } = useMutation({
    mutationFn: createSchedule,
  });

  const { mutateAsync: updateScheduleMutate } = useMutation({
    mutationFn: updateSchedule,
  });

  const fromSchedule = from?.fromSchedule;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const onSubmit = ({ formData, reset, closeModal }: SubmitProps<z.infer<typeof createEditScheduleFormSchema>>) => {
    const { startDate, endDate, practiceDate, diagnosticDate } = formData;
    const scheduleBreaks = textAreaValueToArray(formData.scheduleBreaks)
      .map((s) => parseIntoScheduleBreak(s))
      .filter(isNotErrorString);

    const selectedSchool = allSchools.find((o) => o.id === formData.schoolId);
    if (!selectedSchool) {
      toast({
        title: `Selected school not found`,
      });
      return;
    }
    const scheduleTimeZone = selectedSchool.org.timeZone;
    if (!scheduleTimeZone) {
      toast({
        title: 'Missing time zone',
        description:
          'Selected school does not have a time zone and you did not provide an override time zone. Either update the school to have a time zone or provide an override time zone.',
      });
      return;
    }

    if (!validatePlainDateIntervalLike(startDate, endDate)) {
      toast({
        title: 'Invalid date range',
        description: 'The start date must be before the end date.',
      });
      return;
    }

    const payload: UpdateScheduleOptions = {
      ...formData,
      startPlainDate: startDate,
      endPlainDate: endDate,
      timeZone: scheduleTimeZone,
      scheduleBreaks,
      practicePlainDate: practiceDate,
      diagnosticPlainDate: diagnosticDate,
    };

    const apiCall =
      fromSchedule && from.mode === 'edit'
        ? () =>
            updateScheduleMutate({
              id: fromSchedule.id,
              data: payload,
            })
        : () =>
            createScheduleMutate({
              ...payload,
              classTimes: [], // Created elsewhere after the schedule is created
            });

    return apiCall()
      .then(() => {
        toast({
          title: from?.mode === 'edit' ? 'Schedule updated' : 'Schedule created',
        });
        reset();
        closeModal();
        afterSave?.();
        queryClient.invalidateQueries(getSchedules());
      })
      .catch((err) => {
        toast({
          title: from?.mode === 'edit' ? 'Schedule update failed' : 'Schedule creation failed',
          description: err.message,
        });
      });
  };

  return (
    <FormModal
      modalTitle={modalTitle || 'Create schedule'}
      trigger={trigger || <Button>Create schedule</Button>}
      onSubmit={onSubmit}
      formSchema={createEditScheduleFormSchema}
      dialogContentProps={{ className: 'max-w-2xl' }}
      defaultValues={toDefaultValue(fromSchedule?.timeZone || timeZone, fromSchedule, from?.mode)}
    >
      {({ form }) => {
        return (
          <div>
            <DialogDescription>
              {from?.mode === 'edit'
                ? 'Modify the schedule details'
                : 'Create a schedule that can be used to create student groups and allow teacher signups'}
            </DialogDescription>
            <div className="grid grid-cols-2 gap-2 py-4">
              <FormInputField name="name" label="Schedule name" form={form} inputProps={{ className: 'w-[80%]' }} />
              <FormSingleSelect
                name="schoolId"
                label="School"
                form={form}
                options={allSchools.map((s) => s.id)}
                renderOption={(id) => allSchools.find((s) => s.id === id)?.name ?? id}
                loading={isLoadingSchools}
                triggerProps={{ className: 'w-[80%]' }}
              />
              <FormInputDate name="startDate" label="Start date" form={form} />
              <FormInputDate name="endDate" label="End date" form={form} />
              <FormInputDate name="practiceDate" label="Practice class date" form={form} />
              <FormInputDate name="diagnosticDate" label="Diagnostic class date" form={form} />
              <div className="col-span-2">
                <FormTextArea
                  name="scheduleBreaks"
                  label="Breaks (One per line, format 'MM/DD/YYYY - MM/DD/YYYY')"
                  form={form}
                  textAreaProps={{ placeholder: 'e.g. 03/01/2024 - 03/15/2024' }}
                />
              </div>
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
